import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin, Clock, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { companyInfo, heroImages } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { submitContact } from '../services/api';

const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      await submitContact(formData);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', propertyType: '', message: '' });
      }, 3000);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white relative overflow-hidden" aria-label="Contact Us for Property Management Services">
      {/* Decorative background */}
      <div className="absolute bottom-0 right-0 w-96 h-96 opacity-10">
        <img
          src={heroImages.bougainvillea}
          alt="Tropical bougainvillea decoration"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#000080] bg-[#FFFF00] font-semibold tracking-wider uppercase text-sm mb-4 px-4 py-1 rounded-full">
            {t('contact.badge')}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[#000080] mb-6">
            {t('contact.title')}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact info */}
          <div>
            <div className="bg-gradient-to-br from-[#000080] to-[#000050] rounded-3xl p-8 md:p-10 text-white mb-8">
              <h3 className="font-display text-2xl font-bold mb-6">{t('contact.info')}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#00FFFF]" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{t('contact.location')}</p>
                    <p className="text-white/70">{companyInfo.location}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#00FFFF]" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">WhatsApp</p>
                    <a href="https://wa.me/18292852485" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#00FFFF] transition-colors">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#00FFFF]" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{t('contact.email')}</p>
                    <a href={`mailto:${companyInfo.email}`} className="text-white/70 hover:text-[#00FFFF] transition-colors">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#00FFFF]" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{t('contact.hours')}</p>
                    <p className="text-white/70">{t('contact.hoursWeek')}</p>
                    <p className="text-white/70">{t('contact.hoursSun')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick highlights */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#FFFF00]/20 rounded-xl p-4 text-center border border-[#FFFF00]/30">
                <p className="text-3xl font-bold text-[#000080]">20+</p>
                <p className="text-gray-600 text-sm">{t('contact.yearsExp')}</p>
              </div>
              <div className="bg-[#FFFF00]/20 rounded-xl p-4 text-center border border-[#FFFF00]/30">
                <p className="text-3xl font-bold text-[#000080]">3</p>
                <p className="text-gray-600 text-sm">{t('contact.languages')}</p>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-10">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 rounded-full bg-[#008000]/20 flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-[#008000]" />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#000080] mb-2">
                  {t('contact.form.success')}
                </h3>
                <p className="text-gray-600">
                  {t('contact.form.successMsg')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#000080] mb-2">{t('contact.form.fullName')}</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    className="bg-white border-gray-200 focus:border-[#00FFFF] focus:ring-[#00FFFF]"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#000080] mb-2">{t('contact.form.email')}</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@email.com"
                      required
                      className="bg-white border-gray-200 focus:border-[#00FFFF] focus:ring-[#00FFFF]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#000080] mb-2">{t('contact.form.phone')}</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="bg-white border-gray-200 focus:border-[#00FFFF] focus:ring-[#00FFFF]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#000080] mb-2">{t('contact.form.propertyType')}</label>
                  <select
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-[#00FFFF] focus:outline-none focus:ring-2 focus:ring-[#00FFFF]/20"
                  >
                    <option value="">{t('contact.form.selectProperty')}</option>
                    <option value="apartment">{t('contact.form.apartment')}</option>
                    <option value="villa">{t('contact.form.villa')}</option>
                    <option value="condo">{t('contact.form.condo')}</option>
                    <option value="house">{t('contact.form.house')}</option>
                    <option value="other">{t('contact.form.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#000080] mb-2">{t('contact.form.message')}</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.messagePlaceholder')}
                    rows={4}
                    required
                    className="bg-white border-gray-200 focus:border-[#00FFFF] focus:ring-[#00FFFF]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-tropical bg-[#FFFF00] hover:bg-[#FFD700] text-[#000080] font-bold py-6 text-lg shadow-lg disabled:opacity-50"
                >
                  {isLoading ? 'Sending...' : t('contact.form.send')}
                  <Send className="w-5 h-5 ml-2" />
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
